import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../Button/Button";
import { IconButton } from "../IconButton/IconButton";

export interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

type PageToken = number | "ellipsis";

const WINDOW_SIZE = 5;

function getPageTokens(current: number, pageCount: number): PageToken[] {
  if (pageCount <= WINDOW_SIZE) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const start = Math.min(Math.max(current - 2, 1), pageCount - WINDOW_SIZE + 1);
  const end = start + WINDOW_SIZE - 1;

  const tokens: PageToken[] = [];
  if (start > 1) tokens.push(1);
  if (start > 2) tokens.push("ellipsis");
  for (let p = start; p <= end; p++) tokens.push(p);
  if (end < pageCount - 1) tokens.push("ellipsis");
  if (end < pageCount) tokens.push(pageCount);
  return tokens;
}

export function Pagination({ page, pageCount, onPageChange }: Readonly<PaginationProps>) {
  if (pageCount <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-1">
      <IconButton
        variant="ghost"
        size="sm"
        aria-label="Previous page"
        icon={<ChevronLeft />}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      />
      {getPageTokens(page, pageCount).map((token, index) =>
        token === "ellipsis" ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: a static-length, never-reordered token list; an ellipsis has no identity beyond its own position in the sequence.
          <span key={`ellipsis-${index}`} className="px-1 text-content-tertiary">
            …
          </span>
        ) : (
          <Button
            key={token}
            variant={token === page ? "secondary" : "ghost"}
            size="sm"
            aria-current={token === page ? "page" : undefined}
            onClick={() => onPageChange(token)}
          >
            {token}
          </Button>
        ),
      )}
      <IconButton
        variant="ghost"
        size="sm"
        aria-label="Next page"
        icon={<ChevronRight />}
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
      />
    </div>
  );
}
